import React from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { Controller, ControllerProps, FieldError } from "react-hook-form";
import FormControl from '@mui/material/FormControl';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';

import { FormErrorMessage } from "components/forms";
import { SelectOptionsState } from 'utils/types';

function sortingOtions(options: SelectOptionsState[]) {
    let optionsList = JSON.parse(JSON.stringify(options));
    let results = optionsList?.filter((list: SelectOptionsState) => (list.value && list.label))?.sort((a: any, b: any) => a?.label?.toString().localeCompare(b?.label?.toString()));
    return results;
}

interface SelectProps {
    placeholder?: string;
    label?: string;
    fullWidth?: boolean;
    textFieldProps?: TextFieldProps;
    formLabel?: string;
}

interface Props extends SelectProps, Omit<
    AutocompleteProps<SelectOptionsState, false | true, boolean, true>,
    'error' | 'onChange' | 'required' | 'renderInput'
> {
    name: string;
    rules?: ControllerProps["rules"];
    defaultValue?: SelectOptionsState | SelectOptionsState[];
    control: ControllerProps["control"];
    error?: FieldError;
    onChangeValue?: (data: any) => void;
}

const CustomSelect = React.memo((props: Props) => {
    const { name, control, rules, label, error, placeholder, textFieldProps, options, onChangeValue, defaultValue, formLabel, ...rest } = props;

    return (
        <FormControl fullWidth>
            {formLabel && <span style={{ marginBottom: "8px" }}>{formLabel}</span>}

            <Controller
                name={name}
                control={control}
                rules={rules}
                defaultValue={defaultValue ? defaultValue : props?.multiple ? [] : null}
                render={({ field }) => (
                    <Autocomplete
                        {...field}
                        onChange={(_, data) => {
                            field.onChange(data);
                            if (onChangeValue) {
                                onChangeValue(data);
                            }
                        }}
                        value={field.value || ""}
                        getOptionLabel={(option: any) => option?.label ?? ""}
                        filterSelectedOptions
                        options={options}
                        renderInput={(params: any) => (
                            <TextField
                                variant="outlined"
                                label={props.label ? rules?.required ? `${props.label} *` : props.label : ''}
                                placeholder={placeholder}
                                error={error ? true : false}
                                {...params}
                                {...textFieldProps}
                            />
                        )}
                        {...rest}
                    />
                )}
            />

            <FormErrorMessage error={error} />
        </FormControl>
    )
});

const Select = React.memo((props: SelectProps & Omit<
    AutocompleteProps<SelectOptionsState, false | true, boolean, true>,
    'renderInput'
>) => {
    const { label, placeholder, fullWidth = true, textFieldProps, options, formLabel, ...rest } = props;
    let inputPropsStyle = textFieldProps?.InputProps?.style ? textFieldProps.InputProps.style : {};

    return (
        <FormControl fullWidth={fullWidth}>
            {formLabel && <span className="text-13 font-medium" style={{ marginBottom: "8px" }}>{formLabel}</span>}

            <Autocomplete
                getOptionLabel={(option: any) => option.label}
                filterSelectedOptions
                isOptionEqualToValue={(option, value) => option.value === value.value}
                // getOptionSelected={(option, value) => option?.value === value?.value}
                // options={sortingOtions(options)}
                options={options}
                {...rest}
                renderInput={(params: any) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        label={label}
                        placeholder={placeholder}
                        {...textFieldProps}
                        InputProps={{
                            ...params.InputProps,
                            style: inputPropsStyle
                        }}
                    />
                )}
            />
        </FormControl>
    )
})

export { CustomSelect, Select };